import React, { useState, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Drawer, Typography, IconButton, Box, Button, Checkbox,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, Select, MenuItem, Divider,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import {
  AREA_TYPES, AREA_TYPE_LABELS, AREA_TYPE_COLORS, AREA_CATEGORIES,
  SERVICES, SERVICE_LABELS, MODIFIER_DEFS,
  getAreaTypeLabel, getAreaTypeColor, resolveAreaType,
  isExclusionArea,
} from '../../SiteTakeoff/siteTakeoffConstants';

const useStyles = makeStyles(() => ({
  paper: {
    width: 'calc(100vw - 360px)',
    minWidth: 700,
    background: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 20px',
    borderBottom: '1px solid #e0e0e0',
    background: '#fafafa',
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
    flex: 1,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 20px',
    borderBottom: '1px solid #f0f0f0',
    background: '#FAFAFA',
    flexWrap: 'wrap',
  },
  selectInfo: {
    fontSize: 13,
    color: '#333',
    fontWeight: 500,
    marginRight: 8,
  },
  actionBtn: {
    textTransform: 'none',
    fontSize: 12,
    fontWeight: 500,
  },
  tableWrap: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
    '& th': {
      position: 'sticky',
      top: 0,
      background: '#F9FAFB',
      padding: '8px 12px',
      textAlign: 'left',
      fontSize: 11,
      fontWeight: 600,
      color: '#6B7280',
      textTransform: 'uppercase',
      letterSpacing: 0.3,
      borderBottom: '2px solid #E5E7EB',
      whiteSpace: 'nowrap',
      zIndex: 1,
    },
    '& td': {
      padding: '6px 12px',
      borderBottom: '1px solid #F3F4F6',
      verticalAlign: 'middle',
      whiteSpace: 'nowrap',
    },
    '& tr:hover td': {
      background: '#F9FAFB',
    },
  },
  rowSelected: {
    '& td': {
      background: '#EEF2FF !important',
    },
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    display: 'inline-block',
    verticalAlign: 'middle',
    marginRight: 6,
  },
  cellName: {
    fontWeight: 500,
    maxWidth: 180,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  tag: {
    display: 'inline-block',
    fontSize: 10,
    padding: '1px 6px',
    borderRadius: 3,
    marginRight: 3,
    whiteSpace: 'nowrap',
  },
  irrigNone: {
    background: '#F3F4F6',
    color: '#9CA3AF',
  },
  irrigBasic: {
    background: '#DBEAFE',
    color: '#1D4ED8',
  },
  irrigFull: {
    background: '#D1FAE5',
    color: '#065F46',
  },
  svcChip: {
    display: 'inline-block',
    fontSize: 10,
    padding: '1px 5px',
    borderRadius: 3,
    background: '#F3F4F6',
    color: '#374151',
    marginRight: 2,
    marginBottom: 2,
  },
  modTag: {
    display: 'inline-block',
    fontSize: 10,
    padding: '1px 5px',
    borderRadius: 3,
    background: '#FEF3C7',
    color: '#92400E',
    marginRight: 2,
  },
}));

const formatSF = (n) => (parseFloat(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });

const IRRIGATION_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'basic', label: 'Basic' },
  { value: 'full', label: 'Full' },
];

const IRRIG_CLASS = { none: 'irrigNone', basic: 'irrigBasic', full: 'irrigFull' };

// Get non-default modifiers for display
const getActiveModifiers = (mods) => {
  if (!mods) return [];
  const active = [];
  if (mods.access === 'obstructed') active.push('Obstructed');
  if (mods.slope === 'sloped') active.push('Sloped');
  if (mods.litterExposure === 'high') active.push('High litter');
  if (mods.weedPressure === 'med') active.push('Med weeds');
  if (mods.weedPressure === 'high') active.push('High weeds');
  if (mods.limitedAccess) active.push('Limited access');
  if (mods.blowRestrictions) active.push('Blow restrict');
  return active;
};

const BulkEditDrawer = ({ open, onClose, areas = [], onUpdateArea }) => {
  const classes = useStyles();
  const [selected, setSelected] = useState(new Set());
  const [editField, setEditField] = useState(null);
  const [modalValues, setModalValues] = useState({});
  const [typeMenuAnchor, setTypeMenuAnchor] = useState(null);
  const [groupMenuAnchor, setGroupMenuAnchor] = useState(null);

  const editableAreas = useMemo(() => areas.filter(a => !isExclusionArea(a)), [areas]);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(editableAreas.map(a => a._id || a.id)));
  const selectNone = () => setSelected(new Set());
  const selectByType = (areaType) => {
    const ids = editableAreas.filter(a => resolveAreaType(a.areaType) === areaType).map(a => a._id || a.id);
    setSelected(prev => { const next = new Set(prev); ids.forEach(id => next.add(id)); return next; });
  };

  const uniqueTypes = useMemo(() => {
    const types = new Set();
    editableAreas.forEach(a => types.add(resolveAreaType(a.areaType)));
    return [...types];
  }, [editableAreas]);

  const uniqueGroups = useMemo(() => {
    const map = {};
    editableAreas.forEach(a => {
      if (a.groupId && a.groupName) {
        if (!map[a.groupId]) map[a.groupId] = { id: a.groupId, name: a.groupName, count: 0 };
        map[a.groupId].count++;
      }
    });
    return Object.values(map);
  }, [editableAreas]);

  const selectByGroup = (groupId) => {
    const ids = editableAreas.filter(a => a.groupId === groupId).map(a => a._id || a.id);
    setSelected(prev => { const next = new Set(prev); ids.forEach(id => next.add(id)); return next; });
  };

  const selectedCount = selected.size;
  const allSelected = selectedCount === editableAreas.length && editableAreas.length > 0;

  const openFieldModal = (field) => {
    if (selectedCount === 0) return;
    switch (field) {
      case 'services': setModalValues({ services: [] }); break;
      case 'irrigation': setModalValues({ irrigation: 'none' }); break;
      case 'modifiers':
        setModalValues({ access: null, slope: null, litterExposure: null, weedPressure: null, limitedAccess: null, blowRestrictions: null });
        break;
      case 'areaType': setModalValues({ areaType: AREA_TYPES.GR_CLEAN }); break;
      default: return;
    }
    setEditField(field);
  };

  const applyBulkUpdate = () => {
    if (!editField || selectedCount === 0) return;
    selected.forEach(areaId => {
      let updates;
      switch (editField) {
        case 'services':
          updates = { servicesRequired: [...modalValues.services] };
          break;
        case 'irrigation':
          updates = { modifiers: { irrigationIncluded: modalValues.irrigation } };
          break;
        case 'modifiers': {
          const mods = {};
          Object.entries(modalValues).forEach(([k, v]) => { if (v !== null) mods[k] = v; });
          if (Object.keys(mods).length > 0) updates = { modifiers: mods };
          break;
        }
        case 'areaType':
          updates = { areaType: modalValues.areaType, fillColor: getAreaTypeColor(modalValues.areaType) };
          break;
        default: return;
      }
      if (updates) onUpdateArea(areaId, updates);
    });
    setEditField(null);
  };

  return (
    <>
      <Drawer anchor="right" open={open} onClose={onClose} classes={{ paper: classes.paper }}>
        <div className={classes.header}>
          <Typography className={classes.title}>Bulk Edit Areas</Typography>
          <span style={{ fontSize: 13, color: '#6B7280' }}>{editableAreas.length} areas</span>
          <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </div>

        {/* Toolbar */}
        <div className={classes.toolbar}>
          <span className={classes.selectInfo}>
            {selectedCount > 0 ? `${selectedCount} selected` : 'Select areas to edit'}
          </span>
          <Button size="small" className={classes.actionBtn} onClick={allSelected ? selectNone : selectAll}>
            {allSelected ? 'Deselect all' : 'Select all'}
          </Button>
          <Button size="small" className={classes.actionBtn} onClick={(e) => setTypeMenuAnchor(e.currentTarget)}>
            By type ▾
          </Button>
          {uniqueGroups.length > 0 && (
            <Button size="small" className={classes.actionBtn} onClick={(e) => setGroupMenuAnchor(e.currentTarget)}>
              By group ▾
            </Button>
          )}
          {selectedCount > 0 && (
            <Button size="small" className={classes.actionBtn} onClick={selectNone} style={{ color: '#999' }}>
              Clear
            </Button>
          )}
          <div style={{ flex: 1 }} />
          <Button size="small" variant="outlined" className={classes.actionBtn} disabled={selectedCount === 0}
            onClick={() => openFieldModal('services')}
            style={{ borderColor: selectedCount ? '#C7D2FE' : '#e0e0e0', color: selectedCount ? '#4F46E5' : '#ccc' }}>
            Set Services
          </Button>
          <Button size="small" variant="outlined" className={classes.actionBtn} disabled={selectedCount === 0}
            onClick={() => openFieldModal('irrigation')}
            style={{ borderColor: selectedCount ? '#C7D2FE' : '#e0e0e0', color: selectedCount ? '#4F46E5' : '#ccc' }}>
            Set Irrigation
          </Button>
          <Button size="small" variant="outlined" className={classes.actionBtn} disabled={selectedCount === 0}
            onClick={() => openFieldModal('modifiers')}
            style={{ borderColor: selectedCount ? '#C7D2FE' : '#e0e0e0', color: selectedCount ? '#4F46E5' : '#ccc' }}>
            Set Modifiers
          </Button>
          <Button size="small" variant="outlined" className={classes.actionBtn} disabled={selectedCount === 0}
            onClick={() => openFieldModal('areaType')}
            style={{ borderColor: selectedCount ? '#C7D2FE' : '#e0e0e0', color: selectedCount ? '#4F46E5' : '#ccc' }}>
            Set Type
          </Button>
        </div>

        {/* Select by type popover */}
        {typeMenuAnchor && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1300 }}
            onClick={() => setTypeMenuAnchor(null)}>
            <div style={{
              position: 'absolute',
              top: typeMenuAnchor.getBoundingClientRect().bottom + 4,
              left: typeMenuAnchor.getBoundingClientRect().left,
              background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)', padding: 4,
              maxHeight: 300, overflowY: 'auto', zIndex: 1301,
            }} onClick={(e) => e.stopPropagation()}>
              {uniqueTypes.map(t => (
                <div key={t} onClick={() => { selectByType(t); setTypeMenuAnchor(null); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px',
                    cursor: 'pointer', fontSize: 13, borderRadius: 4 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: AREA_TYPE_COLORS[t] || '#999' }} />
                  {AREA_TYPE_LABELS[t] || t}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Select by group popover */}
        {groupMenuAnchor && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1300 }}
            onClick={() => setGroupMenuAnchor(null)}>
            <div style={{
              position: 'absolute',
              top: groupMenuAnchor.getBoundingClientRect().bottom + 4,
              left: groupMenuAnchor.getBoundingClientRect().left,
              background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)', padding: 4,
              maxHeight: 300, overflowY: 'auto', zIndex: 1301,
            }} onClick={(e) => e.stopPropagation()}>
              {uniqueGroups.map(g => (
                <div key={g.id} onClick={() => { selectByGroup(g.id); setGroupMenuAnchor(null); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px',
                    cursor: 'pointer', fontSize: 13, borderRadius: 4 }}>
                  <span style={{ color: '#6366F1', fontSize: 10 }}>▪</span>
                  <span style={{ flex: 1 }}>{g.name}</span>
                  <span style={{ fontSize: 11, color: '#999' }}>{g.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Table */}
        <div className={classes.tableWrap}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <Checkbox size="small" checked={allSelected} indeterminate={selectedCount > 0 && !allSelected}
                    onChange={allSelected ? selectNone : selectAll} style={{ padding: 2 }} color="primary" />
                </th>
                <th>Name</th>
                <th>Type</th>
                <th style={{ textAlign: 'right' }}>Area (SF)</th>
                <th>Irrigation</th>
                <th>Services</th>
                <th>Modifiers</th>
                <th>Group</th>
              </tr>
            </thead>
            <tbody>
              {editableAreas.map(area => {
                const areaId = area._id || area.id;
                const isSelected = selected.has(areaId);
                const color = getAreaTypeColor(area.areaType);
                const typeLabel = getAreaTypeLabel(area.areaType);
                const irrigation = area.modifiers?.irrigationIncluded || 'none';
                const irrigClass = IRRIG_CLASS[irrigation] || 'irrigNone';
                const svcs = area.servicesRequired || [];
                const activeMods = getActiveModifiers(area.modifiers);
                return (
                  <tr key={areaId} className={isSelected ? classes.rowSelected : ''}
                    onClick={() => toggleSelect(areaId)} style={{ cursor: 'pointer' }}>
                    <td>
                      <Checkbox size="small" checked={isSelected} style={{ padding: 2 }} color="primary" />
                    </td>
                    <td>
                      <span className={classes.dot} style={{ background: color }} />
                      <span className={classes.cellName}>{area.name || typeLabel}</span>
                    </td>
                    <td style={{ color: '#6B7280', fontSize: 12 }}>{typeLabel}</td>
                    <td style={{ textAlign: 'right', fontWeight: 500 }}>{formatSF(area.area)}</td>
                    <td>
                      <span className={`${classes.tag} ${classes[irrigClass]}`}>
                        {irrigation}
                      </span>
                    </td>
                    <td style={{ whiteSpace: 'normal' }}>
                      {svcs.length === 0 ? (
                        <span style={{ color: '#D1D5DB', fontSize: 11 }}>—</span>
                      ) : (
                        svcs.map(s => (
                          <span key={s} className={classes.svcChip}>{SERVICE_LABELS[s] || s}</span>
                        ))
                      )}
                    </td>
                    <td>
                      {activeMods.length === 0 ? (
                        <span style={{ color: '#D1D5DB', fontSize: 11 }}>—</span>
                      ) : (
                        activeMods.map(m => (
                          <span key={m} className={classes.modTag}>{m}</span>
                        ))
                      )}
                    </td>
                    <td style={{ fontSize: 12, color: area.groupName ? '#4338CA' : '#D1D5DB' }}>
                      {area.groupName || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Drawer>

      {/* ===== Field Edit Modals ===== */}

      {/* Services Modal */}
      <Dialog open={editField === 'services'} onClose={() => setEditField(null)}
        maxWidth="sm" fullWidth PaperProps={{ style: { borderRadius: 12 } }}>
        <DialogTitle style={{ fontSize: 16, fontWeight: 600 }}>
          Set Services ({selectedCount} areas)
        </DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 12 }}>
            Select the services to apply. This replaces existing services on selected areas.
          </Typography>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {Object.entries(SERVICES).map(([, svcValue]) => {
              const active = (modalValues.services || []).includes(svcValue);
              return (
                <Chip key={svcValue} label={SERVICE_LABELS[svcValue]} size="small"
                  onClick={() => {
                    setModalValues(prev => ({
                      ...prev,
                      services: active
                        ? prev.services.filter(s => s !== svcValue)
                        : [...(prev.services || []), svcValue],
                    }));
                  }}
                  style={{
                    background: active ? '#1976d2' : 'transparent',
                    color: active ? '#fff' : '#666',
                    borderColor: active ? '#1976d2' : '#ccc',
                    fontSize: 11, height: 26, cursor: 'pointer',
                  }}
                  variant={active ? 'default' : 'outlined'}
                />
              );
            })}
          </div>
        </DialogContent>
        <DialogActions style={{ padding: '12px 24px' }}>
          <Button onClick={() => setEditField(null)} style={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={applyBulkUpdate}
            style={{ textTransform: 'none', boxShadow: 'none' }}>
            Apply to {selectedCount} areas
          </Button>
        </DialogActions>
      </Dialog>

      {/* Irrigation Modal */}
      <Dialog open={editField === 'irrigation'} onClose={() => setEditField(null)}
        maxWidth="xs" fullWidth PaperProps={{ style: { borderRadius: 12 } }}>
        <DialogTitle style={{ fontSize: 16, fontWeight: 600 }}>
          Set Irrigation ({selectedCount} areas)
        </DialogTitle>
        <DialogContent>
          <div style={{ display: 'flex', gap: 8 }}>
            {IRRIGATION_OPTIONS.map(opt => (
              <Button key={opt.value}
                variant={modalValues.irrigation === opt.value ? 'contained' : 'outlined'}
                color={modalValues.irrigation === opt.value ? 'primary' : 'default'}
                onClick={() => setModalValues({ irrigation: opt.value })}
                style={{ flex: 1, textTransform: 'none', fontWeight: 500, boxShadow: 'none' }}>
                {opt.label}
              </Button>
            ))}
          </div>
        </DialogContent>
        <DialogActions style={{ padding: '12px 24px' }}>
          <Button onClick={() => setEditField(null)} style={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={applyBulkUpdate}
            style={{ textTransform: 'none', boxShadow: 'none' }}>
            Apply to {selectedCount} areas
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modifiers Modal */}
      <Dialog open={editField === 'modifiers'} onClose={() => setEditField(null)}
        maxWidth="xs" fullWidth PaperProps={{ style: { borderRadius: 12 } }}>
        <DialogTitle style={{ fontSize: 16, fontWeight: 600 }}>
          Set Modifiers ({selectedCount} areas)
        </DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 12 }}>
            Only changed values will be applied. Leave as "—" to keep existing.
          </Typography>
          {Object.entries(MODIFIER_DEFS).map(([key, def]) => {
            if (key === 'irrigationIncluded') return null;
            const val = modalValues[key];
            return (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 13, flex: 1, minWidth: 120 }}>{def.label}</span>
                {def.type === 'toggle' || def.type === 'select' ? (
                  <FormControl size="small" variant="outlined" style={{ minWidth: 130 }}>
                    <Select value={val === null ? '__skip__' : val}
                      onChange={(e) => {
                        const v = e.target.value;
                        setModalValues(prev => ({ ...prev, [key]: v === '__skip__' ? null : v }));
                      }}>
                      <MenuItem value="__skip__"><em>— no change —</em></MenuItem>
                      {def.options.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                    </Select>
                  </FormControl>
                ) : (
                  <FormControl size="small" variant="outlined" style={{ minWidth: 130 }}>
                    <Select value={val === null ? '__skip__' : val ? 'true' : 'false'}
                      onChange={(e) => {
                        const v = e.target.value;
                        setModalValues(prev => ({ ...prev, [key]: v === '__skip__' ? null : v === 'true' }));
                      }}>
                      <MenuItem value="__skip__"><em>— no change —</em></MenuItem>
                      <MenuItem value="true">Yes</MenuItem>
                      <MenuItem value="false">No</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </div>
            );
          })}
        </DialogContent>
        <DialogActions style={{ padding: '12px 24px' }}>
          <Button onClick={() => setEditField(null)} style={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={applyBulkUpdate}
            style={{ textTransform: 'none', boxShadow: 'none' }}>
            Apply to {selectedCount} areas
          </Button>
        </DialogActions>
      </Dialog>

      {/* Area Type Modal */}
      <Dialog open={editField === 'areaType'} onClose={() => setEditField(null)}
        maxWidth="xs" fullWidth PaperProps={{ style: { borderRadius: 12 } }}>
        <DialogTitle style={{ fontSize: 16, fontWeight: 600 }}>
          Set Area Type ({selectedCount} areas)
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small" variant="outlined">
            <Select value={modalValues.areaType || AREA_TYPES.GR_CLEAN}
              onChange={(e) => setModalValues({ areaType: e.target.value })}>
              {Object.entries(AREA_CATEGORIES).map(([catKey, cat]) => [
                <MenuItem key={`header-${catKey}`} disabled style={{
                  opacity: 1, fontWeight: 600, fontSize: '0.72rem', background: '#f5f5f5',
                }}>{cat.label}</MenuItem>,
                ...cat.types.map(t => (
                  <MenuItem key={t} value={t}>
                    <span style={{
                      width: 10, height: 10, borderRadius: '50%', background: AREA_TYPE_COLORS[t],
                      display: 'inline-block', marginRight: 8, border: '1px solid rgba(0,0,0,0.15)',
                    }} />
                    {AREA_TYPE_LABELS[t]}
                  </MenuItem>
                )),
              ])}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions style={{ padding: '12px 24px' }}>
          <Button onClick={() => setEditField(null)} style={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={applyBulkUpdate}
            style={{ textTransform: 'none', boxShadow: 'none' }}>
            Apply to {selectedCount} areas
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkEditDrawer;
